'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { MessagesSquare, Phone, X } from 'lucide-react';
import {
  REGISTRY_BLOCK,
  pickSiteSocialLinksFromContent,
  SITE_SOCIAL_NETWORK_KEYS,
  type SiteSocialNetworkKey,
} from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveCtaLucideByIconKey } from '../../icons/ctaLucideMap';
import { SocialIcon } from '../../icons/SocialIcon';
import { SITE_SOCIAL_ICONS } from '../../icons/socialNetworks';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';
import { safeTelHref } from '../../lib/safeGuestLinks';
import type { BlockRenderProps } from '../../types';

const NETWORK_LABEL: Record<SiteSocialNetworkKey, string> = {
  facebook: SITE_SOCIAL_ICONS.facebook.title,
  instagram: SITE_SOCIAL_ICONS.instagram.title,
  tiktok: SITE_SOCIAL_ICONS.tiktok.title,
  x: SITE_SOCIAL_ICONS.x.title,
  youtube: SITE_SOCIAL_ICONS.youtube.title,
};

type Arrangement = 'rail' | 'fab';

function resolveArrangement(registryBlockKey?: string): Arrangement {
  if (registryBlockKey === REGISTRY_BLOCK.presentation.contactChannelsFab) return 'fab';
  return 'rail';
}

/**
 * Global chrome — phone + social channels from Local Presence.
 * Live bake omits empty / draft-hidden via `floatChromeHtml` (SSR).
 * Studio React keeps Visible floats (empty → ghosts) in Edit and View so
 * Zone 4 Visibility is observable; draft-hidden mutes in Edit and omits in View.
 * CSS hides the float under 768px site width (Mobil) regardless of Visibility.
 */
export function ContactChannelsFloat({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const arrangement = resolveArrangement(block.registryBlockKey);
  const phone = pickBlockField(block.content, 'phone', locale).trim();
  const hasPhone = Boolean(phone) && phone !== '—';
  const telHref = safeTelHref(phone);
  const phoneIconKey = pickBlockField(block.content, 'phoneIcon', locale);
  const phoneIcon = resolveCtaLucideByIconKey(phoneIconKey) ?? Phone;
  const fabIconKey = pickBlockField(block.content, 'fabIcon', locale);
  const fabIcon = resolveCtaLucideByIconKey(fabIconKey) ?? MessagesSquare;
  const links = pickSiteSocialLinksFromContent(block.content, locale);
  const isEmpty = !hasPhone && links.length === 0;
  const isDraftHidden = block.hidden === true;
  /** Studio: only force the Fab panel open while this block is selected (keeps Zone 3 calm). */
  const forceOpenForEdit = Boolean(editable && isActive && arrangement === 'fab');
  const [open, setOpen] = useState(forceOpenForEdit);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const leaveCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelId = useId();
  const target =
    editable && activeEditTarget
      ? activeEditTarget
      : null;
  const expanded = arrangement === 'fab' ? Boolean(forceOpenForEdit || open) : true;

  useEffect(() => {
    if (arrangement !== 'fab') {
      return;
    }
    setOpen(forceOpenForEdit);
  }, [arrangement, forceOpenForEdit]);

  /* Click outside closes; open is click-only (no hover-open). */
  useEffect(() => {
    if (forceOpenForEdit || arrangement !== 'fab' || !open) return;

    function onPointerDown(event: PointerEvent) {
      const root = rootRef.current;
      const targetNode = event.target;
      if (!root || !(targetNode instanceof Node) || root.contains(targetNode)) return;
      setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [forceOpenForEdit, arrangement, open]);

  useEffect(() => {
    return () => {
      if (leaveCloseTimer.current) clearTimeout(leaveCloseTimer.current);
    };
  }, []);

  /* View + Hidden → omit (live-like). Edit + Hidden → muted ghost below. Empty stays painted when Visible. */
  if (!editable && isDraftHidden) {
    return null;
  }

  const regionLabel = pickChrome('contactChannelsRegion', locale);
  const expandLabel =
    pickBlockField(block.content, 'expandLabel', locale) ||
    pickChrome('contactChannelsExpand', locale);
  const collapseLabel = pickChrome('contactChannelsCollapse', locale);
  const phoneLabel = pickChrome('contactChannelsPhone', locale);
  const socialEmpty = pickChrome('footerSocialEmpty', locale);

  /**
   * Float chrome stays icon-sized (rail + fab). Showing the phone string here
   * blew the Rail panel into a wide rounded card in Zone 3 edit preview —
   * owners edit the number in Zone 4; icon is pickable via `phoneIcon`.
   * Cleared / missing phone: omit the phone control entirely (edit + live).
   */
  const phoneIconNode = editable ? (
    <EditableContent
      as="span"
      fieldKey="phoneIcon"
      editable={editable}
      activeEditTarget={target}
      className="ws-channels__icon-hit"
    >
      <LucideIcon icon={phoneIcon} size="md" className="ws-channels__icon" />
    </EditableContent>
  ) : (
    <LucideIcon icon={phoneIcon} size="md" className="ws-channels__icon" />
  );

  const phoneNode = hasPhone ? (
    telHref && !editable ? (
      <a
        className="ws-channels__link ws-channels__link--phone"
        href={telHref}
        aria-label={`${phoneLabel}: ${phone}`}
      >
        {phoneIconNode}
        <span className="ws-channels__sr">{phone}</span>
      </a>
    ) : (
      <span
        className="ws-channels__link ws-channels__link--phone"
        aria-label={`${phoneLabel}: ${phone}`}
        {...(editable
          ? ({ 'data-edit-child': 'content-field', 'data-field-key': 'phone' } as const)
          : {})}
      >
        {phoneIconNode}
        <span className="ws-channels__sr">{phone}</span>
      </span>
    )
  ) : null;

  const socialList = isEmpty ? (
    <div
      className="ws-channels__empty"
      data-edit-child="content-field"
      data-field-key="socialLinks"
    >
      <ul className="ws-channels__list ws-channels__list--ghost" aria-hidden="true">
        {SITE_SOCIAL_NETWORK_KEYS.slice(0, 3).map((network) => (
          <li key={network}>
            <span className="ws-channels__link ws-channels__link--ghost">
              <SocialIcon icon={network} size="md" />
            </span>
          </li>
        ))}
      </ul>
      <p className="ws-channels__empty-cta">{socialEmpty}</p>
    </div>
  ) : (
    <ul className="ws-channels__list">
      {links.map(({ network, url }) => (
        <li key={network}>
          <a
            className="ws-channels__link"
            href={editable ? '#' : url}
            target={editable ? undefined : '_blank'}
            rel={editable ? undefined : 'noopener noreferrer'}
            aria-label={NETWORK_LABEL[network]}
            onClick={
              editable
                ? (event) => {
                    event.preventDefault();
                  }
                : undefined
            }
            {...(editable
              ? ({ 'data-edit-child': 'content-field', 'data-field-key': 'socialLinks' } as const)
              : {})}
          >
            <SocialIcon icon={network} size="md" />
          </a>
        </li>
      ))}
    </ul>
  );

  const channels = (
    <div className="ws-channels__panel" id={panelId}>
      {phoneNode}
      {socialList}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={[
        'ws-float-chrome',
        'ws-channels',
        `ws-channels--${arrangement}`,
        expanded ? 'ws-channels--open' : '',
        isEmpty ? 'ws-channels--empty' : '',
        isActive ? 'ws-block--active' : '',
        isDraftHidden ? 'ws-block--hidden' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-channels-arrangement={arrangement}
      {...(isDraftHidden ? ({ 'data-block-hidden': 'true' } as const) : {})}
      role="region"
      aria-label={regionLabel}
      onMouseEnter={() => {
        if (forceOpenForEdit || arrangement !== 'fab') return;
        if (leaveCloseTimer.current) {
          clearTimeout(leaveCloseTimer.current);
          leaveCloseTimer.current = null;
        }
      }}
      onMouseLeave={() => {
        if (forceOpenForEdit || arrangement !== 'fab' || !open) return;
        leaveCloseTimer.current = setTimeout(() => {
          leaveCloseTimer.current = null;
          setOpen(false);
        }, 280);
      }}
    >
      {arrangement === 'fab' ? (
        <>
          <button
            type="button"
            className="ws-channels__fab"
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={expanded ? collapseLabel : expandLabel}
            onClick={(event) => {
              if (forceOpenForEdit) return;
              if (open) {
                setOpen(false);
                event.currentTarget.blur();
              } else {
                setOpen(true);
              }
            }}
          >
            <span className="ws-channels__fab-glyph" aria-hidden="true">
              {editable ? (
                <EditableContent
                  as="span"
                  fieldKey="fabIcon"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-channels__fab-icon ws-channels__fab-icon--share"
                >
                  <LucideIcon icon={fabIcon} size="md" />
                </EditableContent>
              ) : (
                <LucideIcon
                  icon={fabIcon}
                  size="md"
                  className="ws-channels__fab-icon ws-channels__fab-icon--share"
                />
              )}
              <LucideIcon
                icon={X}
                size="md"
                className="ws-channels__fab-icon ws-channels__fab-icon--close"
              />
            </span>
          </button>
          {channels}
        </>
      ) : (
        channels
      )}
    </div>
  );
}
